'use client';
import { VALIDITY } from '@/constants';
import type { FlexibleFormVariant } from './FlexibleFormBase';
import { buildServerValidation, createFlexibleFormSubmitErrorValidation } from './flexibleFormShared';
import {
    getPromoCodeFieldErrorMessage,
    getPromoCodeFormErrorMessage,
    type PromoCodeErrorState,
} from './promoCodeEntryValidation';
import { FlexibleFormAlert, type FlexibleFormAlertTone } from './FlexibleFormAlert';
import { getMicroservicesCountryCode, type MicroservicesCountryCode } from '@/services/microservices/usedCarEligibility';
import { submitPromoCodeValidationRequest } from '@/services/microservices/promoCode';
import { getTrialActivationBaseUrl } from '@/services/runtimeConfig';

const PROMO_SUCCESS_URLS = {
    us: {
        campaign: 'https://care.siriusxm.com/subscribe/checkout/purchase/streaming/organic/creds',
        default: 'https://care.siriusxm.com/subscribe/checkout/purchase/streaming/self-pay/organic',
    },
    ca: {
        campaign: 'https://care.siriusxm.ca/subscribe/checkout/purchase/streaming/organic/creds',
        default: 'https://care.siriusxm.ca/subscribe/checkout/purchase/streaming/organic',
    },
} as const;

const PROMO_VIEW_OTHER_OFFERS_URLS = {
    us: 'https://care.siriusxm.com/subscribe/checkout/purchase/streaming/self-pay/organic',
    ca: 'https://care.siriusxm.ca/subscribe/checkout/purchase/streaming/organic',
} as const;

const STREAMING_ORGANIC_PATH = '/subscribe/checkout/purchase/streaming/organic';
const STREAMING_SELF_PAY_ORGANIC_PATH = '/subscribe/checkout/purchase/streaming/self-pay/organic';

const getRuntimeEnvironment = () =>
    process.env.SXM_ENV || process.env.npm_config_env || process.env.NEXT_PUBLIC_ENV || 'sandbox.preview';

function isProdEnvironment() {
    return getRuntimeEnvironment().toLowerCase().includes('prod');
}

function getNonProdPromoCheckoutBaseUrl() {
    const trialBaseUrl = getTrialActivationBaseUrl();

    if (!trialBaseUrl) {
        return PROMO_SUCCESS_URLS.us.default;
    }

    return trialBaseUrl.replace(/\/activate\/trial\/?$/, STREAMING_ORGANIC_PATH);
}

function getPromoSuccessUrls(countryCode: MicroservicesCountryCode) {
    if (isProdEnvironment()) {
        return PROMO_SUCCESS_URLS[countryCode];
    }

    const nonProdBaseUrl = getNonProdPromoCheckoutBaseUrl();
    return { campaign: nonProdBaseUrl, default: nonProdBaseUrl };
}

function getPromoViewOtherOffersUrl(countryCode: MicroservicesCountryCode) {
    if (isProdEnvironment()) {
        return PROMO_VIEW_OTHER_OFFERS_URLS[countryCode];
    }

    const nonProdCheckoutBaseUrl = getNonProdPromoCheckoutBaseUrl();
    const viewOtherOffersUrl = new URL(nonProdCheckoutBaseUrl);
    viewOtherOffersUrl.pathname = viewOtherOffersUrl.pathname.replace(
        STREAMING_ORGANIC_PATH,
        STREAMING_SELF_PAY_ORGANIC_PATH,
    );

    return viewOtherOffersUrl.toString();
}

interface PromoCodeValidationError {
    errorCode?: string;
    fieldErrors?: Array<{
        errorCode?: string;
    }>;
}

function getCaseInsensitiveSearchParam(searchParams: URLSearchParams, targetKey: string) {
    const normalizedTargetKey = targetKey.toLowerCase();

    for (const [key, value] of searchParams.entries()) {
        if (key.toLowerCase() === normalizedTargetKey) {
            return value;
        }
    }

    return undefined;
}

function deleteCaseInsensitiveSearchParam(searchParams: URLSearchParams, targetKey: string) {
    const normalizedTargetKey = targetKey.toLowerCase();
    const keysToDelete = Array.from(searchParams.keys()).filter((key) => key.toLowerCase() === normalizedTargetKey);

    keysToDelete.forEach((key) => {
        searchParams.delete(key);
    });
}

function copyCurrentQueryParams(targetUrl: URL, currentUrl: URL) {
    currentUrl.searchParams.forEach((value, key) => {
        targetUrl.searchParams.append(key, value);
    });
}

function buildPromoCodeViewOtherOffersUrl(countryCode: MicroservicesCountryCode) {
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(getPromoViewOtherOffersUrl(countryCode));
    copyCurrentQueryParams(targetUrl, currentUrl);
    return targetUrl.toString();
}

function buildPromoCodeRedirectUrl(
    countryCode: MicroservicesCountryCode,
    promoCode: string,
    smsOffer: boolean | undefined,
) {
    const promoSuccessUrls = getPromoSuccessUrls(countryCode);
    const currentUrl = new URL(window.location.href);
    const campaign = getCaseInsensitiveSearchParam(currentUrl.searchParams, 'campaign');
    const hasCampaign = campaign !== undefined;
    const langpref = getCaseInsensitiveSearchParam(currentUrl.searchParams, 'langpref');
    const targetUrl = new URL(
        hasCampaign ? promoSuccessUrls.campaign : promoSuccessUrls.default,
    );

    copyCurrentQueryParams(targetUrl, currentUrl);

    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'promocode');
    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'campaign');
    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'langpref');
    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'smsOffer');

    targetUrl.searchParams.set('promocode', promoCode);

    if (hasCampaign) {
        targetUrl.searchParams.set('campaign', campaign || '');
    } else {
        targetUrl.searchParams.set('smsOffer', String(Boolean(smsOffer)));
    }

    if (langpref !== undefined) {
        targetUrl.searchParams.set('langpref', langpref);
    }

    return targetUrl.toString();
}

function normalizeErrorCode(errorCode?: string) {
    return `${errorCode || ''}`.toUpperCase();
}

function getPromoCodeErrorState(error: PromoCodeValidationError | undefined): PromoCodeErrorState | undefined {
    const errorCodes = [error?.errorCode, ...(error?.fieldErrors || []).map((fieldError) => fieldError.errorCode)]
        .map(normalizeErrorCode)
        .filter(Boolean);

    if (errorCodes.some((errorCode) => errorCode.includes('ALREADY_REDEEMED'))) {
        return 'redeemed';
    }

    if (errorCodes.some((errorCode) => errorCode.includes('OUTSIDE_START_AND_END_DATE'))) {
        return 'expired';
    }

    if (errorCodes.length > 0) {
        return 'invalid';
    }

    return undefined;
}

function isPromoCodeValidationError(error: unknown): error is PromoCodeValidationError {
    return typeof error === 'object' && error !== null && ('errorCode' in error || 'fieldErrors' in error);
}

function setPromoCodeFieldError(
    promoCodeFieldName: string,
    promoCodeErrorState: PromoCodeErrorState,
    viewOtherOffersUrl: string,
    context: Parameters<Exclude<FlexibleFormVariant['customSubmit'], undefined>>[1],
) {
    const { validations, setValidations, setSubmitState } = context;
    const serverValidation = buildServerValidation(validations, promoCodeFieldName);
    const promoCodeFieldMessage = getPromoCodeFieldErrorMessage(promoCodeErrorState, viewOtherOffersUrl);
    const tone: FlexibleFormAlertTone = promoCodeErrorState === 'invalid' ? 'critical' : 'warning';
    const alert = <FlexibleFormAlert tone={tone} message={promoCodeFieldMessage} />;

    if (!serverValidation) {
        setValidations(createFlexibleFormSubmitErrorValidation(validations, promoCodeFieldMessage, tone));
        setSubmitState('untouched');
        return;
    }

    setValidations({
        ...validations,
        [promoCodeFieldName]: {
            ...serverValidation,
            validations: { ...serverValidation.validations, regex: false },
            helperText: { ...serverValidation.helperText, regex: '' },
        },
        form: {
            value: '',
            invalid: VALIDITY.invalid,
            validations: { ...validations.form.validations, formerror: true },
            helperText: { ...validations.form.helperText, formerror: alert },
        },
    });
    setSubmitState('untouched');
}

function setPromoCodeFormError(
    viewOtherOffersUrl: string,
    context: Parameters<Exclude<FlexibleFormVariant['customSubmit'], undefined>>[1],
) {
    const { validations, setValidations, setSubmitState } = context;

    setValidations(
        createFlexibleFormSubmitErrorValidation(validations, getPromoCodeFormErrorMessage(viewOtherOffersUrl)),
    );
    setSubmitState('untouched');
}

export function createPromoCodeCustomSubmit(
    promoCodeFieldName?: string,
): FlexibleFormVariant['customSubmit'] {
    return async (data, context) => {
        const countryCode = getMicroservicesCountryCode();
        const viewOtherOffersUrl = buildPromoCodeViewOtherOffersUrl(countryCode);

        if (!promoCodeFieldName) {
            setPromoCodeFormError(viewOtherOffersUrl, context);
            return;
        }

        const promoCode = `${(data.get(promoCodeFieldName) as string) || ''}`.trim();

        try {
            const response = await submitPromoCodeValidationRequest(countryCode, {
                marketingPromoCode: promoCode,
                streaming: true,
            });

            if (response.status !== 'SUCCESS') {
                setPromoCodeFormError(viewOtherOffersUrl, context);
                return;
            }

            window.location.assign(buildPromoCodeRedirectUrl(countryCode, promoCode, response.data?.smsOffer));
        } catch (error) {
            const promoCodeErrorState = getPromoCodeErrorState(
                isPromoCodeValidationError(error) ? error : undefined,
            );

            if (promoCodeErrorState) {
                setPromoCodeFieldError(promoCodeFieldName, promoCodeErrorState, viewOtherOffersUrl, context);
                return;
            }

            setPromoCodeFormError(viewOtherOffersUrl, context);
        }
    };
}
